import {types, flow} from 'mobx-state-tree';
import * as api from '../../services/api';
import * as consts from '../../consts';

// tslint:disable
const OtaAccountModel = types.model('OtaAccountModel', {
  id: types.identifierNumber,
  userName: types.maybe(types.string),
  email: types.string,
  source: types.string,
  token: types.maybe(types.string),
  state: types.string,
  linkUrl: types.maybe(types.string),
  otaHostId: types.maybe(types.string),
  phone: types.maybe(types.string),
  avatar: types.maybe(types.string),
  isVerified: types.maybe(types.boolean),
  airlockId: types.maybe(types.number)
});

const PaginationModel = types.model('PaginationModel', {
  offset: types.optional(types.number, 0),
  limit: types.optional(types.number, 20),
  total: types.optional(types.number, 0),
  current: types.optional(types.number, 1)
});

const OtaAccountsListModel = types
  .model('OtaAccountsListModel', {
    isLoading: types.optional(types.boolean, false),
    entries: types.map(OtaAccountModel),
    pagination: types.optional(PaginationModel, {}),
    dataNewOtaAccount: types.optional(
      types
        .model({
          email: types.maybe(types.string),
          password: types.maybe(types.string),
          teamId: types.maybe(types.number),
          source: types.maybe(types.string),
          userName: types.maybe(types.string),
          token: types.maybe(types.string),
          otaHostId: types.maybe(types.string)
        })
        .actions(self => ({
          clearData: () => {
            self.email = '';
            self.password = '';
            self.teamId = undefined;
            self.source = '';
            self.userName = '';
            self.token = '';
            self.otaHostId = '';
          },
          onChangeEmail: (email: string) => {
            self.email = email;
          },
          onChangePassword: (pass: string) => {
            self.password = pass;
          },
          onChangeTeamId: (teamId: number) => {
            self.teamId = teamId;
          },
          onChangeSource: (source: string) => {
            self.source = source;
          },
          onChangeUserName: (userName: string) => {
            self.userName = userName;
          },
          onChangeToken: (token: string) => {
            self.token = token;
          },
          onChangeOtaHostId: (otaHostId: string) => {
            self.otaHostId = otaHostId;
          }
        })),
      {}
    ),
    dataUpdate: types.optional(
      types
        .model({
          id: types.maybe(types.number),
          email: types.maybe(types.string),
          password: types.maybe(types.string),
          teamId: types.maybe(types.number),
          source: types.maybe(types.string),
          userName: types.maybe(types.string),
          token: types.maybe(types.string),
          otaHostId: types.maybe(types.string)
        })
        .actions(self => {
          return {
            onChangeEmail: (email: string) => {
              self.email = email;
            },
            onChangeSource: (source: string) => {
              self.source = source;
            },
            onChangeUserName: (userName: string) => {
              self.userName = userName;
            },
            onChangeOtaHostId: (otaHostId: string) => {
              self.otaHostId = otaHostId;
            },
            onChangeToken: (token: string) => {
              self.token = token;
            }
          };
        }),
      {}
    )
  })
  .actions(self => ({
    loadEntries: flow(function*(
      team_id: number,
      limit: number,
      offset: number
    ) {
      self.isLoading = true;
      const params = {
        team_id,
        _limit: limit || consts.PAGINATION__DEFAULT_PER_PAGE,
        _offset: offset || 0
      };
      const response = yield api.getOtaAccounts(params);
      if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.otaAccounts
      ) {
        // const {offset, limit} = response.data.metadata;
        let otaAccounts = response.data.otaAccounts.reduce(
          (results: {[id: string]: any}, r: any) => (
            (results[String(r.id)] = r), results
          ),
          {}
        );
        self.pagination.limit = limit;
        self.pagination.offset = offset;
        self.entries = otaAccounts;
      }
      self.isLoading = false;
    }),
    createNew: flow(function*(callback: any) {
      const email = self.dataNewOtaAccount.email;
      const source = self.dataNewOtaAccount.source;
      const teamId = self.dataNewOtaAccount.teamId;
      const userName = self.dataNewOtaAccount.userName;

      if (!email || !source || !teamId || !userName) {
        callback('error', 'Please enter field: source, email, user name');
        return;
      }
      const body = {
        email: email,
        password: self.dataNewOtaAccount.password,
        teamId: teamId,
        source: source,
        userName: self.dataNewOtaAccount.userName,
        token: self.dataNewOtaAccount.token,
        otaHostId: self.dataNewOtaAccount.otaHostId
      };

      const response = yield api.createOtaAccount(body);
      if (response.status && response.data) {
        const otaAccount = response.data.otaAccount;
        self.entries.set(String(otaAccount.id), otaAccount);
        self.dataNewOtaAccount.clearData();
        callback('success', 'Created ota account successfully');
      } else {
        callback('error', response.data.message);
      }
    }),
    removeOtaAccount: flow(function*(otaAccountId: number) {
      const response = yield api.removeOtaAccount(otaAccountId);
      if (response.status === 200) {
        self.entries.delete(String(otaAccountId));
      }
    }),
    updateOtaAccount: flow(function *(otaAccountId: number, callback: Function) {
      const body = {
        userName: self.dataUpdate.userName,
        email: self.dataUpdate.email,
        source: self.dataUpdate.source,
        otaHostId: self.dataUpdate.otaHostId,
        token: self.dataUpdate.token,
      };
      const response = yield api.updateOtaAccount(otaAccountId, body);
      if (response.status < 400) {
        Object.assign(self.dataUpdate, response.data.otaAccount);
        self.entries.set(String(otaAccountId), response.data.otaAccount);
        callback('success', 'Updated ota account successfully');
      } else {
        callback('error', response.data.message);
      }
    }),
    onChangeDataUpdate: flow(function*(otaAccountId: number) {
      const response = yield api.getOtaAccount(otaAccountId);
      if (response.status === 200 && response.data) {
        self.dataUpdate = response.data.otaAccount;
      }
    }),
    clearEntries: () => {
      self.entries.clear();
    }
  }));

export type OtaAccountsListModelType = typeof OtaAccountsListModel.Type;
export default OtaAccountsListModel;



// WEBPACK FOOTER //
// ./src/models/OtaAccountsListModel/OtaAccountListModel.ts